// client/src/cognitive-flows/components/canvas/blockEdit.js
//
// Converters between a block result's stored shape and the column-major
// `{ columns, rows }` grid the manual-table editor already speaks.
//
// The point is that a data table, an agent's viz table and a chart's series
// are all editable with ONE grid component. A chart is a two-column table
// (label, value) wearing a different hat, so treating it as one means the
// chart editor is free rather than a third bespoke form.

/** Array-of-objects (`result.rows`, `viz.table`) → grid. */
export function gridFromRows(rows) {
    const list = Array.isArray(rows) ? rows : [];
    // Union of keys, not the first row's: rows can disagree on shape, and a
    // key present only on row 5 is still a real column the user must be able
    // to see and edit.
    const columns = [...new Set(list.flatMap((r) => Object.keys(r || {})))];
    return {
        columns,
        rows: list.map((r) => columns.map((c) => {
            const v = r?.[c];
            return v === undefined || v === null ? '' : String(v);
        })),
    };
}

/**
 * Grid → array-of-objects. Values stay strings here; the server coerces
 * numeric-looking cells back to numbers on save, so that rule lives in exactly
 * one place rather than being half-applied on both sides.
 */
export function rowsFromGrid(grid) {
    const columns = Array.isArray(grid?.columns) ? grid.columns : [];
    const rows = Array.isArray(grid?.rows) ? grid.rows : [];
    if (columns.length === 0) return [];
    return rows.map((r) => {
        const out = {};
        columns.forEach((c, i) => { out[c] = Array.isArray(r) ? (r[i] ?? '') : ''; });
        return out;
    });
}

/** Chart_data → a two-column grid: the category axis and the series. */
export function gridFromChart(cd) {
    const x = Array.isArray(cd?.charx_axis_data_points) ? cd.charx_axis_data_points : [];
    const yRaw = Array.isArray(cd?.chart_y_axis_data_points) ? cd.chart_y_axis_data_points : [];
    const y = Array.isArray(yRaw[0]) ? yRaw[0] : yRaw;
    const n = Math.min(x.length, y.length);
    return {
        // The value column is the SERIES, so it is named after series_name —
        // not after chart_header, which it used to borrow. The chart's title is
        // now its own editable field, and having two controls silently write
        // the same value was the confusing part: typing a title only to have
        // the grid's column name overwrite it on save.
        columns: ['Label', cd?.series_name || 'Value'],
        rows: Array.from({ length: n }, (_, i) => [String(x[i] ?? ''), String(y[i] ?? '')]),
    };
}

/**
 * A two-column grid back into Chart_data, preserving everything else on the
 * chart (type, title, axis labels, legend settings) and taking the SERIES name
 * from the value column's header.
 *
 * `chart_header` falls back to the column name only when the chart has no
 * title at all — which is what a chart created before titles were editable
 * looks like, and keeps its old header rather than becoming "Chart".
 */
export function chartFromGrid(grid, previous) {
    const rows = Array.isArray(grid?.rows) ? grid.rows : [];
    const columns = Array.isArray(grid?.columns) ? grid.columns : [];
    const seriesName = String(columns[1] || '').trim();
    return {
        ...(previous || {}),
        chart_header: previous?.chart_header || seriesName || 'Chart',
        chart_type: previous?.chart_type || 'bar',
        ...(seriesName ? { series_name: seriesName } : {}),
        charx_axis_data_points: rows.map((r) => String(r?.[0] ?? '')),
        chart_y_axis_data_points: rows.map((r) => {
            const n = Number(r?.[1]);
            return Number.isFinite(n) ? n : 0;
        }),
    };
}
